// Copyright (c) 2020-2023 dātma, inc™
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { store } from '@/store'
import { fetchWithTimeout } from '@/common/shared'
import { ehrBaseUrl, ehrCrudUrl, omicsBaseUrl, omicsFastUrl } from '@/settings'

const useNewOmicsApi = store.getters.featureToggles.useNewOmicsApi

const samplesBaseUrl = `${ehrBaseUrl}/sample_id`
const reportUrl = `${ehrCrudUrl}/report-store/patient-identifier/`
const omicsdsSampleUrl = `${omicsFastUrl}/sample`

const patientQuery = 'patient'
const filterQuery = 'samples'

const callSampleEhrWithTimeout = async (data, query, baseUrl) => {
  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${baseUrl}?${query}=${encodeURIComponent(data)}`
    const response = await fetchWithTimeout(url, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return []
    }
    const result = await response.json()
    if (!result) { return [] }
    // Translate the response to the format expected by the frontend.
    return result
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
  return []
}

export const checkOmicsdsFilterSamples = async (filteredSamples, samples) => {
  const queryParams = new URLSearchParams()

  samples.forEach((sample) => {
    queryParams.append('samples', sample.sample)
  })

  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${omicsdsSampleUrl}?${queryParams.toString()}`
    const response = await fetchWithTimeout(url, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return
    }
    const results = await response.json()
    const tempFiltered = samples.filter((sample) => results.includes(sample.sample))
    filteredSamples.push(...tempFiltered)
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
}

export const checkSampleForFilter = async (baseUrl, filteredSamples, query, sample) => {
  try {
    const fetchParams = {
      method: 'HEAD',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${baseUrl}?${query}=${encodeURIComponent(sample.sample)}`
    const response = await fetchWithTimeout(url, fetchParams)
    // console.log('response?', response)
    if (!response.ok) {
      // 404 means we don't have genomics data for this sample
      return
    }
    filteredSamples.push(sample)
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
}

const callFilterSamplesWithTimeout = async (samples, id, query, baseUrl) => {
  const filteredSamples = []
  let reports = []
  const promises = []

  //add any samples with genomics data
  if (useNewOmicsApi) {
    await checkOmicsdsFilterSamples(filteredSamples, samples)
  } else {
    for (const sample of samples) {
      promises.push(checkSampleForFilter(baseUrl, filteredSamples, query, sample))
    }
    await Promise.allSettled(promises)
  }

  //add any samples that have reports that we don't already have
  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${reportUrl}${encodeURIComponent(id)}?show_missing_files=false&report_type=GENOMICS`
    const response = await fetchWithTimeout(url, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return filteredSamples
    }
    const result = await response.json()
    reports = result.reports
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }

  // console.log('filtered samples before adding reports: ', [...filteredSamples])

  if (!reports) { return filteredSamples }

  const sampleAccessionNumbers = samples.map((sample) => sample.accession_number)

  // All reports should go in as a 'sample'
  // if a Sample and a report are tied, the sample should get the uuid and mime type and push
  // if a report does not have a sample, just push the report object. The sample card component will distinguish
  for (const report of reports) {
    const accessionNumbersOrOtherIds = report.identifiers.map((id) => id.identifier)
    let reportIsTiedToSample = false
    for (const accession of accessionNumbersOrOtherIds) {
      if (sampleAccessionNumbers.includes(accession)) {
        const sampleMatch = samples.find((sample) => sample.accession_number === accession)
        if (!sampleMatch || !accession) { continue }
        // TODO: we may have many reports to one sample. we should consolidate.
        const sample = sampleMatch
        sample.mimetype = report.mimetype
        sample.uuid = report.uuid
        if (!filteredSamples.includes(sample)) {
          report.date = report.ordering_date
          report.source_lab = report.identifiers.find(i => i.identifier_type === 'lab_name')?.identifier || null
          // We just push the report since this was a sample with no genomics data
          filteredSamples.push(report)
        }
        reportIsTiedToSample = true
        break
      }
    }
    // This is the case where we add a report that doesn't tie to a sample
    // the shape of the object will be different; we will use that to determine
    // the type of 'genomics card' that represents it on the front end
    if (!reportIsTiedToSample) {
      report.date = report.ordering_date
      report.source_lab = report.identifiers.find(i => i.identifier_type === 'lab_name')?.identifier || null
      filteredSamples.push(report)
    }
  }

  // console.log('filteredSamples after reports: ', filteredSamples)
  return filteredSamples
}

export const getTrialsLink = async (id) => {
  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${ehrBaseUrl}/trials_link?gdb_id=${encodeURIComponent(id)}`
    const response = await fetchWithTimeout(url, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return []
    }
    const result = await response.json()
    if (!result) { return [] }
    // Translate the response to the format expected by the frontend.
    return result
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
  return []
}

export const getSamples = async (patientId) =>
  await callSampleEhrWithTimeout(patientId, patientQuery, samplesBaseUrl)

export const filterSamples = async (samples, id) =>
  await callFilterSamplesWithTimeout(samples, id, filterQuery, omicsBaseUrl)
